import amplitudeOld from "amplitude-js";

import { AMPLITUDE_KEY, PALTA_DATA_PLATFORM_KEY } from "@/constants/env";
import { IS_PROD_ENV } from "@/constants/env";
import { WebEventTypes } from "@/constants/event";
import { removeUrlProtocol } from "@/utils/removeUrlProtocol";
import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";

import { getFunnelFoxDeviceId } from "../funnelFoxHelpers";
import { withSessionReplay } from "../settings";

import { Tracker } from "./common";

export type EventProps = {
    [key: string]: amplitude.Types.ValidPropertyType | undefined | null;
};

const PALTA_DATA_PLATFORM_INSTANCE_NAME = "palta_data_platform";
const DEFAULT_TELEMETRY_API =
    process.env.REACT_APP_TELEMETRY || "telemetry.zing.paltabrain.com/v1/amplitude";
const PALTA_API = window.location.pathname.includes("/funnel")
    ? process.env.REACT_APP_FUNNEL_TELEMETRY || DEFAULT_TELEMETRY_API
    : DEFAULT_TELEMETRY_API;
const SESSION_REPLAY_COVERAGE_TEST = withSessionReplay ? 1 : 0;
const SESSION_REPLAY_COVERAGE = IS_PROD_ENV ? 0.01 : SESSION_REPLAY_COVERAGE_TEST; // prod: only 1% of sessions will be captured
const INIT_SESSION_REPLAY_FROM_SCREEN_NUMBER = 2;

const sessionReplayTracking = sessionReplayPlugin({ sampleRate: SESSION_REPLAY_COVERAGE });

class AmplitudeInstance extends Tracker {
    private readonly _name: string = "";
    private readonly _key: string | undefined = undefined;
    private readonly _apiEndpoint: string | undefined = undefined;

    private _propertiesQueue: EventProps = {};
    private _eventsQueue: { event: string; eventProps?: EventProps }[] = [];
    private _instance: amplitude.Types.BrowserClient | undefined;
    private _screenViewEventCount = 0;
    private _uuid: string | null = null;
    private _isSessionReplayInitialized = false;
    private _isManuallyLocked = true; // Lock for first visit tracking

    constructor(key: string, name?: string, apiEndpoint?: string) {
        super();

        this._key = key;
        this._name = name ?? "default_tracking";
        this._apiEndpoint = apiEndpoint;
    }

    init() {
        if (this._key && !this._isInitialized) {
            const serverUrl = this._apiEndpoint
                ? {
                      serverUrl: this._apiEndpoint,
                  }
                : {};

            this._instance = amplitude.createInstance();

            this._instance
                .init(this._key, "", {
                    instanceName: this._name,
                    ...serverUrl,
                    autocapture: false,
                    deviceId: getFunnelFoxDeviceId(),
                })
                .promise.then(() => {
                    this._isInitialized = true;
                    this.proceedPropertiesQueue();
                    this.proceedEventsQueue();
                    this._instance?.setUserId(this._uuid || "");
                });
        }

        if (this._isStopped) {
            this._isStopped = false;
        }
    }

    createIdentify(props: EventProps) {
        const identify = new amplitude.Identify();

        Object.entries(props).forEach(([key, value]) => {
            value && identify?.set(key, value);
        });

        return identify;
    }

    setAmplitudeProperties(props: EventProps) {
        if (this._isInitialized && !this._isStopped) {
            const identify = this.createIdentify(props);
            this._instance?.identify(identify);
        } else {
            this._propertiesQueue = {
                ...this._propertiesQueue,
                ...props,
            };
        }
    }

    track(event: string, eventProps?: EventProps) {
        // Queue events if manually locked OR not initialized OR stopped
        if (this._isManuallyLocked || !this._isInitialized || this._isStopped) {
            this._eventsQueue.push({ event, eventProps });
            return;
        }

        this._instance?.track(event, eventProps);

        if (event === WebEventTypes.VIEW_EVENT) {
            this._screenViewEventCount++;
        }

        this.initSessionReplay(eventProps?.screen as string);
    }

    unlockQueue(flushQueue = true) {
        this._isManuallyLocked = false;
        // Flush queue if requested and already initialized and not stopped
        if (flushQueue && this._isInitialized && !this._isStopped) {
            this.proceedEventsQueue();
        }
    }

    setUserId(uuid: string) {
        if (this._instance) {
            this._instance?.setUserId(uuid);
        } else {
            this._uuid = uuid;
        }
    }

    flush(): Promise<void> {
        const result = this._instance?.flush?.();
        return result?.promise != null ? result.promise : Promise.resolve();
    }

    private proceedPropertiesQueue() {
        this.setAmplitudeProperties(this._propertiesQueue);
    }

    private proceedEventsQueue() {
        // Don't flush if manually locked (waiting for FUNNEL_ENTRY)
        if (this._isManuallyLocked) {
            return;
        }

        const queueCopy = [...this._eventsQueue];
        this._eventsQueue = [];
        queueCopy.forEach(({ event, eventProps }) => this.track(event, eventProps));
    }

    private initSessionReplay(screen?: string) {
        if (!this._isSessionReplayInitialized) {
            const isRightQuestionScreen =
                this._screenViewEventCount === INIT_SESSION_REPLAY_FROM_SCREEN_NUMBER;
            const screens = /(paywall)|(email)|(password)/g;
            const isRightFirstScreen = !!screen?.match(screens)?.length;

            if (isRightQuestionScreen || isRightFirstScreen) {
                this._instance?.add(sessionReplayTracking);
                this._isSessionReplayInitialized = true;
            }
        }
    }
}

// TODO: remove when content-type for PALTA_API will be changed
class AmplitudeInstanceOld extends Tracker {
    private readonly _name: string | undefined = undefined;
    private readonly _key: string | undefined = undefined;
    private readonly _apiEndpoint: string | undefined = undefined;

    private _propertiesQueue: EventProps = {};
    private _eventsQueue: { event: string; eventProps?: EventProps }[] = [];
    private _isManuallyLocked = true; // Lock for first visit tracking

    constructor(key: string, name?: string, apiEndpoint?: string) {
        super();

        this._key = key;
        this._name = name;
        this._apiEndpoint = apiEndpoint;
    }

    init() {
        if (this._key && !this._isInitialized) {
            amplitudeOld.getInstance(this._name).init(
                this._key,
                undefined,
                this._apiEndpoint
                    ? {
                          apiEndpoint: removeUrlProtocol(this._apiEndpoint),
                          deviceId: getFunnelFoxDeviceId(),
                      }
                    : undefined
            );
            this._isInitialized = true;
            this.proceedPropertiesQueue();
            this.proceedEventsQueue();
        }

        if (this._isStopped) {
            this._isStopped = false;
        }
    }

    setAmplitudeProperties(props: EventProps) {
        const instance = amplitudeOld.getInstance(this._name);
        if (this._isInitialized && !this._isStopped) {
            instance.setUserProperties(props);
        } else {
            this._propertiesQueue = {
                ...this._propertiesQueue,
                ...props,
            };
        }
    }

    track(event: string, eventProps?: EventProps) {
        const instance = amplitudeOld.getInstance(this._name);
        // Queue events if manually locked OR not initialized OR stopped
        if (this._isManuallyLocked || !this._isInitialized || this._isStopped) {
            this._eventsQueue.push({ event, eventProps });
            return;
        }

        instance.logEvent(event, eventProps);
    }

    unlockQueue(flushQueue = true) {
        this._isManuallyLocked = false;
        // Flush queue if requested and already initialized and not stopped
        if (flushQueue && this._isInitialized && !this._isStopped) {
            this.proceedEventsQueue();
        }
    }

    setUserId(uuid: string) {
        amplitudeOld.getInstance(this._name).setUserId(uuid);
    }

    private proceedPropertiesQueue() {
        this.setAmplitudeProperties(this._propertiesQueue);
    }

    private proceedEventsQueue() {
        // Don't flush if manually locked (waiting for FUNNEL_ENTRY)
        if (this._isManuallyLocked) {
            return;
        }

        const queueCopy = [...this._eventsQueue];
        this._eventsQueue = [];
        queueCopy.forEach(({ event, eventProps }) => this.track(event, eventProps));
    }
}

export const AmplitudeTracker = new AmplitudeInstance(AMPLITUDE_KEY);
export const PaltaDataPlatformTracker = new AmplitudeInstanceOld(
    PALTA_DATA_PLATFORM_KEY,
    PALTA_DATA_PLATFORM_INSTANCE_NAME,
    PALTA_API
);
